import { useEffect, useMemo, useRef, useState } from 'react';
import {
  type StyleSnapshot,
  endConfirmButtonState,
  hasSnapshotContent,
  styleRecapLine,
} from '../lib/endScene';
import {
  FACET_LABEL,
  FEEDBACK_FACETS,
  type FeedbackFacet,
  type FeedbackSelection,
  type StyleFeedback,
  type VerbalDeclared,
  computeFeedback,
  flattenSelection,
  initialSelectionFromPayload,
  isVerbalDeclared,
  parseFeedbackPalette,
  parseVerbalDeclared,
} from '../lib/styleFeedback';
import { useDemoStore } from '../store';
import { Icon } from './Icon';

/**
 * S9 End scene (R13 U1 — the terminal goodbye; R14 B1 — now an IN-CALL style-feedback walkthrough).
 *
 * Field evidence (dev-74, 2026-07-08; user #3b): tapping Done on the close card "reverted to the
 * old product screen" — R13 made 'end' a real terminal scene. Field evidence (dev-75, user note):
 * the interactive style walkthrough used to fire AFTER hang-up, so the visitor saw it only once the
 * call had ended. R14 B1 moves the experience INTO the call, here in the S9 end scene:
 *
 *   1. "We've captured some of your style preferences — here they are." Gigi's captured facet chips
 *      arrive **pre-selected** (initialSelection). R26 U1 — a chip whose value the visitor SAID ALOUD
 *      (declared:verbal, R44) renders with a distinct "you mentioned" affordance (waveform glyph +
 *      stronger brass ring + a small facet-level "you mentioned" note), so a spoken declaration reads
 *      back visibly, separate from Gigi's inferred/tapped read. Inferred-only pre-ticks stay plain
 *      (grounding law). Wire: voice-capture-contract.md §36 (FROZEN) — per-chip provenance rides
 *      `style_snapshot.chips[facet]=[{value,preTicked?,source?}]`; `parseVerbalDeclared` reads the
 *      `source:'verbal'` pre-ticks and `initialSelectionFromPayload` unions the pre-ticks into the
 *      seeded selection (so a chips-only spoken value like "Brass" shows selected). No `chips` map →
 *      every pre-tick renders plain (graceful pre-R26 fallback).
 *   2. A small palette per facet lets the visitor **add** ones she missed (tap to add).
 *   3. Tapping a captured chip **deselects** it (Gigi got that one wrong).
 *   4. **Confirm** → `style-feedback {kept, removed, added}` (R31 §12) → a thank-you beat → the call
 *      winds down (the server drives the goodbye/hang-up; Gigi acknowledges the corrections aloud).
 *
 * Skippable: "Looks right" confirms as-is (kept = all captured, removed/added empty); the small
 * "skip" also confirms-as-is. The post-hangup walkthrough surface is removed (VoiceDemo no longer
 * shows StyleSnapshotWalkthrough after a clean disconnect; R31 disables the server side).
 *
 * Presentation only. The snapshot + palette are parsed tolerantly upstream (lib/endScene.ts,
 * lib/styleFeedback.ts) from the server's `ui.scene {scene:'end'}` payload; a missing payload still
 * renders the confirm surface (captured empty → the visitor builds the read from the palette).
 *
 * Design language: editorial, tokens only, brass accent, foil-gradient headline, .reveal motion
 * (reduced-motion safe via tokens), no emoji.
 */

type Props = {
  /** Gigi's read of the visitor's style — parsed from the end-scene payload (may be empty). */
  snapshot: StyleSnapshot;
  /**
   * R14 B1 — the raw end-scene payload, so the feedback palette (the additional per-facet values the
   * visitor can add) is parsed tolerantly. Optional — a static fallback palette is used when absent.
   */
  rawPayload?: unknown;
  /**
   * R14 B1 — Confirm → send `style-feedback {kept, removed, added}` to the server (persists the
   * declared correction). Best-effort; no-op when disconnected. After it fires, the thank-you beat
   * shows and the server winds the call down.
   */
  onStyleFeedback?: (feedback: StyleFeedback) => void;
  /**
   * R17 Q3 (R34 §16) — the debounced chip-state signal. Called with the flat list of currently
   * selected chip-value tokens on every toggle AND once for the initial pre-selected set, so Gigi can
   * discuss the CHIPS accurately before the visitor confirms (dev-79 turns 31–38: she recited the
   * four PRODUCTS instead). Context-only; the declared write is `onStyleFeedback` on Confirm.
   */
  onStyleChipsUpdated?: (selected: string[]) => void;
  /** "Start a new conversation" → a full session reset (the ONLY way off the terminal end scene). */
  onNewConversation: () => void;
  conversationId?: string;
  // R22 K3 — the R21 (X1) `email-capture-over-end` fallback is RETIRED (USER-SPECIFIED). The special
  // email screen is NOT part of the flow: the Type composer is the ONE out-of-flow capture channel
  // (§29 / K2). The former props (contactFields / onCloseEmail / onCardScan / closeEmailAck /
  // specSheetSendState) that only fed that fallback are removed. See the K3 root cause in the R22
  // review log: the fallback was architecturally unreachable — the server's `emit_scene` terminal-end
  // guard swallowed the `capture_email → ui.scene:close` emission BEFORE it hit the wire whenever the
  // end scene was current, so the client nonce could never be raised (dev-88 turns 47–53, ×3).
};

/**
 * A single facet block: captured chips (tap to deselect) + palette chips (tap to add).
 *
 * R26 U1 — a captured chip whose value the visitor **said aloud** (`declared:verbal`, in `verbal`)
 * renders with a distinct "you mentioned" affordance: a waveform (spoken) glyph instead of the plain
 * check, a slightly stronger brass ring, and — when any chip in the facet is verbal — a small "you
 * mentioned" line under the facet label. It stays a normal selected chip: tap to untick works
 * identically, and once ticked-off it drops back to the plain palette chip (the verbal provenance is
 * a read-back of what was heard, not a lock). Inferred-only pre-ticks keep the plain check treatment
 * (grounding law — only explicit declarations get the "you mentioned" flag).
 */
function FacetEditor({
  facet,
  selected,
  palette,
  verbal,
  onToggle,
}: {
  facet: FeedbackFacet;
  selected: string[];
  palette: string[];
  verbal: VerbalDeclared;
  onToggle: (value: string) => void;
}) {
  const selectedLower = new Set(selected.map((v) => v.toLowerCase()));
  // The full option set for this facet: everything selected + the palette's un-selected additions.
  const paletteOnly = palette.filter((v) => !selectedLower.has(v.toLowerCase()));
  if (selected.length === 0 && paletteOnly.length === 0) return null;
  const hasVerbal = verbal[facet].length > 0;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-baseline gap-2">
        <p className="text-xs uppercase tracking-wide text-faint">{FACET_LABEL[facet]}</p>
        {hasVerbal && (
          <span
            className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-brass"
            data-testid={`end-verbal-note-${facet}`}
          >
            <Icon name="waveform" size={11} />
            you mentioned
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {selected.map((v) => {
          const spoken = isVerbalDeclared(verbal, facet, v);
          return (
            <button
              key={`sel-${v}`}
              type="button"
              onClick={() => onToggle(v)}
              aria-pressed
              data-verbal={spoken ? 'true' : undefined}
              data-testid={spoken ? `end-chip-verbal-${v}` : `end-chip-selected-${v}`}
              className={`inline-flex items-center gap-1 rounded-full bg-brass-soft text-brass text-xs px-2.5 py-1.5 transition-transform duration-1 ease-physical active:scale-95 min-h-[36px] ${
                spoken ? 'font-medium ring-1 ring-brass/60' : 'ring-1 ring-brass/30'
              }`}
              title={spoken ? 'You mentioned this — tap to remove' : undefined}
            >
              <Icon name={spoken ? 'waveform' : 'check'} size={12} />
              {v}
            </button>
          );
        })}
        {paletteOnly.map((v) => (
          <button
            key={`pal-${v}`}
            type="button"
            onClick={() => onToggle(v)}
            aria-pressed={false}
            data-testid={`end-chip-palette-${v}`}
            className="inline-flex items-center gap-1 rounded-full bg-fill text-subtle hover:text-ink text-xs px-2.5 py-1.5 ring-1 ring-line transition-all duration-1 ease-physical active:scale-95 min-h-[36px]"
          >
            <Icon name="plus" size={11} />
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}

export function EndScene({
  snapshot,
  rawPayload,
  onStyleFeedback,
  onStyleChipsUpdated,
  onNewConversation,
  // `conversationId` stays on Props (call-site stability — VoiceDemo passes it) but is no longer
  // consumed here: the only consumer was the retired R21 (X1) email-capture card scan (K3).
}: Props) {
  const hasContent = hasSnapshotContent(snapshot);
  // R29 X3.2 — a one-line "what we learned" recap, built ONLY from the snapshot the server already
  // sends (styleRecapLine reads `summary`/`read` or joins the captured facet values — no new field).
  // null when there's nothing captured worth reciting → the recap card is not rendered (grounding law:
  // read back only what was captured).
  const recap = useMemo(() => styleRecapLine(snapshot), [snapshot]);
  // R22 K3 — the R21 (X1) email-capture-over-end fallback is RETIRED. The end scene is now purely the
  // style-snapshot reveal + walkthrough + goodbye; an out-of-flow email/phone ask routes to the Type
  // composer (§29 / K2 pulse), never a card mounted over the goodbye.
  const palette = useMemo(() => parseFeedbackPalette(rawPayload, snapshot), [rawPayload, snapshot]);
  // R26 U1 — the per-facet set of pre-ticked values the visitor SAID ALOUD (declared:verbal), from the
  // §36 `chips` map (voice-capture-contract.md §36, FROZEN — the captured fixture). Only chips flagged
  // `source:'verbal'` get the "you mentioned" affordance; `source:'inferred'` stays plain (grounding
  // law). Empty when the emit carries no `chips` → every pre-tick renders plain (pre-R26 fallback).
  const verbal = useMemo(() => parseVerbalDeclared(rawPayload), [rawPayload]);
  // R26 U1 — seed the selection from the legacy pre-selected read UNIONED with the §36 `chips`
  // pre-ticks, so a `chips`-only pre-tick (a spoken value the legacy list doesn't carry — the
  // fixture's "Brass") shows SELECTED (§36.1: preTicked:true = renders selected).
  const [selection, setSelection] = useState<FeedbackSelection>(() =>
    initialSelectionFromPayload(snapshot, rawPayload)
  );
  const [confirmed, setConfirmed] = useState(false);
  // R16 V3 — has the visitor touched a chip yet? Drives the prominent-Confirm affordance AND the
  // best-effort auto-submit: while dirty & unconfirmed we mirror the live diff into the store so
  // useVoice's disconnect() can flush it on wind-down (hang-up / new-conversation / disconnect).
  const [dirty, setDirty] = useState(false);
  const setPendingStyleFeedback = useDemoStore((s) => s.setPendingStyleFeedback);

  // Mirror the current unconfirmed diff to the store whenever the touched selection changes; clear
  // it once confirmed (an explicit Confirm already sent it — nothing left to auto-flush).
  useEffect(() => {
    if (confirmed || !dirty) {
      setPendingStyleFeedback(null);
      return;
    }
    setPendingStyleFeedback(computeFeedback(snapshot, selection));
  }, [confirmed, dirty, selection, snapshot, setPendingStyleFeedback]);

  // R17 Q3 (R34 §16) — emit the current chip state, debounced (~200ms), so Gigi's context tracks the
  // chips the visitor has selected (she conflated chips with products, dev-79 turns 31–38). Fires on
  // the INITIAL pre-selected set (once rendered) AND on every toggle. Debounced so a rapid multi-chip
  // flurry sends one settled payload; a re-send of an unchanged flat selection is suppressed (the
  // server treats it as a context note either way, but this keeps the wire quiet). Read the callback
  // through a ref so it isn't a dependency of the debounce. Cleaned up on unmount.
  const chipsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onStyleChipsUpdatedRef = useRef(onStyleChipsUpdated);
  onStyleChipsUpdatedRef.current = onStyleChipsUpdated;
  const lastEmittedChipsRef = useRef<string | null>(null);
  useEffect(() => {
    // Once confirmed the walkthrough is done — no more chip-state emissions.
    if (confirmed) return;
    const selected = flattenSelection(selection);
    const key = selected.map((v) => v.toLowerCase()).join(' ');
    if (key === lastEmittedChipsRef.current) return; // unchanged — suppress the re-send
    if (chipsTimerRef.current !== null) clearTimeout(chipsTimerRef.current);
    chipsTimerRef.current = setTimeout(() => {
      chipsTimerRef.current = null;
      lastEmittedChipsRef.current = key;
      onStyleChipsUpdatedRef.current?.(selected);
    }, 200);
    return () => {
      if (chipsTimerRef.current !== null) {
        clearTimeout(chipsTimerRef.current);
        chipsTimerRef.current = null;
      }
    };
  }, [selection, confirmed]);

  function toggle(facet: FeedbackFacet, value: string) {
    setDirty(true);
    setSelection((prev) => {
      const cur = prev[facet];
      const has = cur.some((v) => v.toLowerCase() === value.toLowerCase());
      return {
        ...prev,
        [facet]: has ? cur.filter((v) => v.toLowerCase() !== value.toLowerCase()) : [...cur, value],
      };
    });
  }

  function confirm() {
    onStyleFeedback?.(computeFeedback(snapshot, selection));
    setPendingStyleFeedback(null);
    setConfirmed(true);
  }

  // ── Thank-you beat (after Confirm / skip) — the call winds down (server drives goodbye). ──
  if (confirmed) {
    return (
      <div className="reveal flex flex-col gap-6" data-testid="end-scene">
        <div className="flex flex-col gap-2">
          <span className="grid h-14 w-14 place-items-center rounded-full bg-brass-soft text-brass">
            <Icon name="sparkle" size={26} />
          </span>
          <h3 className="font-display text-2xl leading-tight text-foil">
            Thank you for visiting Godgift.
          </h3>
          <p className="text-sm text-subtle leading-relaxed max-w-sm" data-testid="end-thanks">
            Noted — I&apos;ll pass that on. It was a pleasure showing you around; the team will
            curate your spec sheet around what you loved.
          </p>
        </div>
        {/* R29 X3.3 — the HONEST spec-sheet status line on the farewell card. Email send is deferred
            until Resend is provisioned, so the truthful state is "queued for the team" — never a
            claimed delivery (accuracy/brand posture, project-brief §6). A quiet carved caption. */}
        <div
          className="field-inset flex items-center gap-2.5 px-4 py-3"
          data-testid="end-spec-status"
        >
          <Icon name="package" size={15} className="shrink-0 text-brass" />
          <p className="text-sm leading-relaxed text-subtle">
            Your spec sheet is queued for the team — they&apos;ll follow up with your selection.
          </p>
        </div>
        <div className="flex justify-center pt-1">
          <button
            type="button"
            data-testid="end-new-conversation-btn"
            onClick={onNewConversation}
            className="btn-ghost inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm text-subtle"
          >
            <Icon name="plus" size={14} />
            Start a new conversation
          </button>
        </div>
      </div>
    );
  }

  const totalSelected =
    selection.styles.length +
    selection.finishes.length +
    selection.materials.length +
    selection.colourTones.length;

  return (
    <div className="reveal flex flex-col gap-6" data-testid="end-scene">
      {/* Thanks — the warm goodbye, foil-gradient headline (the payoff moment). */}
      <div className="flex flex-col gap-2">
        <span className="grid h-14 w-14 place-items-center rounded-full bg-brass-soft text-brass">
          <Icon name="swatchPalette" size={26} />
        </span>
        <h3 className="font-display text-2xl leading-tight text-foil">
          Here&apos;s the read I got on your taste
        </h3>
        <p className="text-sm text-subtle leading-relaxed max-w-sm">
          {hasContent
            ? 'I captured some of your style preferences from our chat. Tap to remove anything that’s off, or add what I missed — then confirm.'
            : 'Tell me a little about your taste — tap what fits, and I’ll curate your spec sheet around it.'}
        </p>
      </div>

      {/* R29 X3.2 — the "what we learned" recap: a quiet one-line card summarising the captured read,
          above the interactive chips. Rendered ONLY when the snapshot carries something (grounding
          law); built purely from the existing payload (styleRecapLine) — no new server field. */}
      {recap && (
        <div className="field-inset flex items-start gap-2.5 px-4 py-3" data-testid="end-recap">
          <Icon name="sparkle" size={15} className="mt-0.5 shrink-0 text-brass" />
          <p className="text-sm leading-relaxed text-ink">
            <span className="text-subtle">What I picked up on: </span>
            {recap}
          </p>
        </div>
      )}

      {/* Interactive style-feedback editor — captured chips (deselect) + palette (add). */}
      <div className="surface-card p-4 flex flex-col gap-4" data-testid="end-snapshot">
        <div className="flex items-center gap-2">
          <Icon name="swatchPalette" size={18} className="text-brass" />
          <p className="font-display text-lg text-ink">Your style snapshot</p>
        </div>
        <div className="flex flex-col gap-4">
          {FEEDBACK_FACETS.map((facet) => (
            <FacetEditor
              key={facet}
              facet={facet}
              selected={selection[facet]}
              palette={palette[facet]}
              verbal={verbal}
              onToggle={(v) => toggle(facet, v)}
            />
          ))}
        </div>
      </div>

      {/* R18 X2 — a SINGLE confirm affordance (the R16 V3 change accidentally rendered two: the
          primary AND a ghost that BOTH read "Looks right" when nothing was toggled — a filled + ghost
          duplicate on the screen, both calling confirm()). One button now: it confirms the read
          (as-is when untouched, or the edits when touched). Once the visitor TOUCHES a chip it turns
          prominent (accent fill + brass ring-pulse + the "save my changes" hint) so they know their
          edits only persist on Confirm — the dev-77 M3 gap was a silent live state. It sends the
          {kept,removed,added} diff and winds the call down either way. */}
      <div className="flex flex-col gap-2">
        {(() => {
          // R18 X2 — one button; its label + prominence come from the unit-tested pure helper.
          const btn = endConfirmButtonState(dirty, totalSelected);
          return (
            <button
              type="button"
              data-testid="end-confirm-btn"
              data-dirty={dirty ? 'true' : undefined}
              onClick={confirm}
              className={`inline-flex w-fit items-center gap-2 rounded-full px-6 py-3 text-sm font-display transition-all duration-2 ease-physical ${
                btn.prominent ? 'btn-accent confirm-attention scale-[1.02]' : 'btn-primary'
              }`}
            >
              <Icon name="check" size={15} />
              {btn.label}
            </button>
          );
        })()}
        {dirty && (
          <p className="text-[11px] text-brass leading-relaxed" data-testid="end-dirty-hint">
            Tap confirm to save your changes — it genuinely helps the team curate your spec sheet.
          </p>
        )}
      </div>

      {/* Quiet new-conversation affordance — the full reset (also the only way off 'end'). */}
      <div className="flex justify-center pt-1">
        <button
          type="button"
          data-testid="end-new-conversation-btn"
          onClick={onNewConversation}
          className="btn-ghost inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs text-faint"
        >
          <Icon name="plus" size={13} />
          Start a new conversation
        </button>
      </div>
    </div>
  );
}
